import MOMENT from 'moment'
import QUERY, { clone, filter, isNumber, find } from 'lodash'
import readXlsxFile, { readSheetNames } from 'read-excel-file/web-worker'
import imageCompressor from 'browser-image-compression'
import { createPopper } from '@popperjs/core'
import { pdfCompressor } from './pdf-compressor'

export default {

  methods: {

    calculateAge (birthDate) {
      if (birthDate) {
        const birth = new Date(birthDate)
        const today = new Date()

        let age = today.getFullYear() - birth.getFullYear()
        const monthDiff = today.getMonth() - birth.getMonth()

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
          age--
        }

        return age
      }

      return 0
    },

    async compressFile (file, onProgress = progress => progress) {
      return new Promise((resolve, reject) => {
        if (file.type === 'application/pdf') {
          if (file.size <= 3024000) { // 3MB
            onProgress(100)

            return resolve(file)
          }

          pdfCompressor(file, onProgress).then(resolve).catch(reject)
        } else {
          if (file.size <= 350000) { // 0.35MB
            onProgress(100)

            return resolve(file)
          }

          const useBackgroundProcessor = {
            maxIteration: 8,
            maxSizeMB: 0.35,
            useWebWorker: typeof Worker === 'function',
            maxWidthOrHeight: 3900,
            onProgress
          }

          imageCompressor(file, useBackgroundProcessor).then(resolve).catch(reject)
        }
      })
    },

    async isDocumentUploadLimitReached (id, options = { document_types: [], uploaded_files: [], files: [] }) {
      const transformDocuments = clone(options.document_types).map(
        option => Object.assign(option, { id: Number(option.id) })
      )
      const documentType = find(transformDocuments, { id: Number(id) })

      if (documentType) {
        const uploadLimit = documentType?.upload_limit || ''

        if (isNumber(uploadLimit) && Number(uploadLimit) >= 0) {
          const transformUploadedFiles = clone(options.uploaded_files).map(
            option => Object.assign(option, { document_type_id: Number(option.document_type_id) })
          )
          const uploadedFilesByDocument = filter(transformUploadedFiles, { document_type_id: Number(id) })
          const totalFilesByDocument = uploadedFilesByDocument.length + options.files.length

          if (totalFilesByDocument > uploadLimit) {
            return {
              type: 'attachments',
              message: `The uploaded files for this ${documentType?.document_type_name} must be lesser than or equal to ${uploadLimit}.`
            }
          }
        }

        return {
          type: null,
          message: null
        }
      }

      return {
        type: 'document_type_id',
        message: 'The document type is required.'
      }
    },

    preventAriaHidden ({ target }, closeCallback = () => null) {
      if (target) {
        target.blur()
        closeCallback()
      } else {
        closeCallback()
      }
    },

    isCreatedWithinTheWeek (date) {
      const dateInMoment = MOMENT(date).format('YYYY-MM-DD HH:mm:ss')
      const currentDate = MOMENT().format('YYYY-MM-DD HH:mm:ss')

      const startOfDay = MOMENT(dateInMoment).format('YYYY-MM-DD 00:00:00')
      const endOfDay = MOMENT(dateInMoment).add(3, 'days').format('YYYY-MM-DD 23:59:59')

      return MOMENT(currentDate).isBetween(
        MOMENT(startOfDay),
        MOMENT(endOfDay)
      )
    },

    isMobile () {
      // /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userOfficer)
      return window.screen.width <= 576 || window.innerWidth <= 576 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userOfficer)
    },

    scaleOnMobile (first, second) {
      if (this.isMobile()) {
        return Number(first.mobile) - Number(second.mobile)
      }

      return Number(second.index) - Number(first.index)
    },

    removeSelectedOnMobile (row, _key) {
      return this.isMobile() ? row.key !== 'selected' : true
    },

    tableRowClass (row, selector) {
      if (row && selector === 'row') {
        return row.last_seen ? 'table-seen-request' : 'table-unseen-request'
      }
      return ''
    },

    tableRowNumber (table, index) {
      return Number(table.currentPage) * Number(table.perPage) - Number(table.perPage) + (Number(index) + 1)
    },

    iconState (icon, isActive) {
      return `fa-xl ${isActive ? 'fad' : 'fat'} ${icon} mr-1`
    },

    statusByVariant (status = 'Pending', variant = '') {
      switch (status) {
        case 'Pending': {
          return variant ? `${variant}-dark` : 'dark'
        }
        case 'Processing': {
          return variant ? `${variant}-warning` : 'warning'
        }
        case 'Disapproved': {
          return variant ? `${variant}-danger` : 'danger'
        }
        case 'Canceled': {
          return variant ? `${variant}-danger` : 'danger'
        }
        default: {
          return variant ? `${variant}-success` : 'success'
        }
      }
    },

    generateId (length) {
      const numbers = '1234567890'
      const lettersUppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const lettersLowercase = 'abcdefghijklmnopqrstuvwxyz'
      const characters = `${numbers}${lettersUppercase}${lettersLowercase}`

      let result = ''

      const charactersLength = characters.length

      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }

      return result
    },

    /**
     * Display a SweetAlert confirmation dialog with customizable options
     * @param {Object} options - Configuration options for the SweetAlert dialog
     * @param {string} [options.title='Confirmation'] - Title of the dialog
     * @param {string|Function} [options.html='Save this Record?'] - HTML content of the dialog
     * @param {string} [options.confirmButtonText='Save'] - Text for the confirm button
     * @param {boolean} [options.showCancelButton=true] - Whether to show the cancel button
     * @param {string} [options.cancelButtonText='Cancel'] - Text for the cancel button
     * @param {boolean} [options.showDenyButton=false] - Whether to show the deny button
     * @param {string} [options.denyButtonText='No'] - Text for the deny button
     * @param {boolean} [options.showLoaderOnConfirm=true] - Whether to show a loader on confirm
     * @param {boolean} [options.allowEscapeKey=false] - Whether to allow escape key to close
     * @param {boolean} [options.heightAuto=false] - Whether to auto-adjust height
     * @param {boolean} [options.buttonsStyling=false] - Whether to use default button styling
     * @param {boolean} [options.allowOutsideClick=false] - Whether to allow clicking outside to close
     * @param {string} [options.width='25rem'] - Width of the dialog
     * @param {Object} [options.customClass] - Custom CSS classes for dialog elements
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalConfirm (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        html: 'Save this Record?',
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        showDenyButton: false,
        denyButtonText: 'No',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        heightAuto: false,
        buttonsStyling: false,
        allowOutsideClick: false,
        target: document.getElementById('swal-output'),
        width: '25rem'
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          denyButton: 'btn btn-sm btn-secondary ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    /**
     * Display a SweetAlert confirmation dialog with remarks/textcluster input field
     * @param {Object} options - Configuration options for the SweetAlert dialog
     * @param {string} [options.title='Confirmation'] - Title of the dialog
     * @param {string|Function} [options.html] - HTML content of the dialog
     * @param {string} [options.input='textcluster'] - Input type, default is textcluster
     * @param {string} [options.inputLabel='Remarks'] - Label for the input field
     * @param {boolean} [options.inputAutoFocus=true] - Whether to autofocus the input field
     * @param {string} [options.width='25rem'] - Width of the dialog
     * @param {Object} [options.inputAttributes] - Attributes for the input field
     * @param {number} [options.inputAttributes.rows=10] - Number of rows for the textcluster
     * @param {string} [options.inputAttributes.name='remarks'] - Name attribute for the textcluster
     * @param {string} [options.inputAttributes.autocapitalize='off'] - Autocapitalization setting
     * @param {number} [options.remarksMaxLength=2500] - Maximum length of remarks
     * @param {boolean} [options.remarksRequired] - Whether remarks are required
     * @param {string} [options.confirmButtonText='Save'] - Text for the confirm button
     * @param {boolean} [options.showCancelButton=true] - Whether to show the cancel button
     * @param {string} [options.cancelButtonText='Discard'] - Text for the cancel button
     * @param {boolean} [options.showLoaderOnConfirm=true] - Whether to show a loader on confirm
     * @param {boolean} [options.allowEscapeKey=false] - Whether to allow escape key to close
     * @param {boolean} [options.buttonsStyling=false] - Whether to use default button styling
     * @param {boolean} [options.heightAuto=false] - Whether to auto-adjust height
     * @param {boolean} [options.allowOutsideClick=false] - Whether to allow clicking outside to close
     * @param {Function} [options.preConfirm] - Function to execute before confirming
     * @param {Object} [options.customClass] - Custom CSS classes for dialog elements
     * @returns {Promise<Object>} The SweetAlert result with remarks value
     */
    swalConfirmWithRemarks (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        html: 'Save this Record?',
        input: 'textarea',
        inputLabel: 'Remarks',
        inputAutoFocus: true,
        target: document.getElementById('swal-output'),
        width: '25rem',
        inputAttributes: {
          rows: 10,
          name: 'remarks',
          autocapitalize: 'off',
          maxlength: options?.remarksMaxLength || 2500,
          placeholder: options?.remarksRequired ? 'enter remarks' : 'enter remarks ( optional )'
        },
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Discard',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        buttonsStyling: false,
        heightAuto: false,
        allowOutsideClick: false,
        preConfirm: remarks => {
          if (typeof options?.preConfirm === 'function') {
            this.$swal.disableInput()
            return options?.preConfirm(remarks)?.finally(() => {
              this.$swal.enableInput()
            })
          }
        },
        inputValidator: remarks => {
          return new Promise(resolve => {
            if (options?.remarksMaxLength) {
              if (remarks?.length > Number(options?.remarksMaxLength)) {
                return resolve(
                  `The remarks field must be atleast lesser than or equal to ${options?.remarksMaxLength} characters`
                )
              }
            }

            if (options?.remarksRequired) {
              if (remarks?.length <= 0) {
                return resolve(
                  'The remarks field is required'
                )
              }
            }

            return resolve()
          })
        },
        didOpen: () => {
          const textArea = document.getElementById('swal2-input')
          if (textArea) {
            textArea.onkeydown = element => {
              const remarks = element.target.value

              if (options?.remarksMaxLength) {
                if (remarks?.search(/\n/g)) {
                  element.target.value = remarks.replace(/\n/g, '')
                }
              }
            }

            textArea.onchange = element => {
              const remarks = element.target.value
              if (remarks?.length > 0) {
                this.$swal.resetValidationMessage()
              }
            }
          }
        }
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          input: 'swal-input my-1 mx-3',
          inputLabel: 'd-flex align-self-start justify-content-start font-weight-bold col-12 px-3',
          validationMessage: 'align-self-start justify-content-start bg-white px-2 my-0 text-danger',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    /**
     * Display a SweetAlert success dialog
     * @param {string|Function} [html='Record has been saved'] - HTML content of the dialog
     * @param {string} [title='Successful'] - Title of the dialog
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalSuccess (html = 'No Response', title = 'Successful') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-success border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-primary'
        },
        target: document.getElementById('swal-output'),
        width: '25rem'
      })
    },

    /**
     * Display a SweetAlert error/validation dialog
     * @param {string|Function} [html='<h6>Your inputs seem to be incorrect or incomplete, please check and try again.</h6>'] - HTML content of the dialog
     * @param {string} [title='Validation Error'] - Title of the dialog
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalInvalid (html = '<h6>Your inputs seem to be incorrect or incomplete, please check and try again.</h6>', title = 'Validation Error') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-danger border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-danger'
        },
        target: document.getElementById('swal-output'),
        width: '25rem'
      })
    },

    calculateDropPosition (dropDownList, component, { width }) {
      dropDownList.style.width = width
      const popper = createPopper(component.$refs.toggle, dropDownList, {
        // placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top'
              )
            }
          }
        ]
      })
      return () => popper.destroy()
    },

    calculateArrowScroller (scrollbar) {
      const secondToLastChild = scrollbar.children[scrollbar.children.length - 2]
      if (secondToLastChild.getBoundingClientRect().right <= scrollbar.getBoundingClientRect().right) {
        scrollbar.firstChild.style.display = 'none'
        scrollbar.lastChild.style.display = 'none'
      } else {
        scrollbar.firstChild.style.display = 'none'
        scrollbar.lastChild.style.display = 'block'
      }
    },

    attachArrowScroller (selector = '.nav-customer-tabs') {
      const scrollbar = document.querySelector(selector)
      if (scrollbar) {
        scrollbar.firstChild.addEventListener('click', () => {
          scrollbar.scrollLeft -= 150
        })

        scrollbar.lastChild.addEventListener('click', () => {
          scrollbar.scrollLeft += 150
        })

        scrollbar.addEventListener('scroll', () => {
          const scrollLeft = scrollbar.scrollLeft
          const scrollWidth = scrollbar.scrollWidth
          const clientWidth = scrollbar.clientWidth

          const scrollBasis = (scrollWidth * 0.10)

          if ((scrollLeft + clientWidth + 10) >= scrollWidth) {
            scrollbar.lastChild.style.display = 'none'
          } else if (scrollLeft <= scrollBasis) {
            scrollbar.lastChild.style.display = 'block'
          }

          if ((scrollLeft + clientWidth + 10) >= scrollWidth || scrollLeft > scrollBasis) {
            scrollbar.firstChild.style.display = 'block'
          } else if (scrollLeft <= scrollBasis) {
            scrollbar.firstChild.style.display = 'none'
          }
        })

        window.addEventListener('resize', () => {
          this.calculateArrowScroller(scrollbar)
        })

        this.calculateArrowScroller(scrollbar)
      }
    },

    detachArrowScroller (selector = '.nav-customer-tabs') {
      const scrollbar = document.querySelector(selector)
      if (scrollbar) {
        scrollbar.firstChild.removeEventListener('click', null)
        scrollbar.lastChild.removeEventListener('click', null)
        scrollbar.removeEventListener('scroll', null)
        window.removeEventListener('resize', null)
      }
    },

    preventEnterKey (event) {
      if (event.keyCode === 13) {
        event.preventDefault()
      }
    },

    validateImportedTask (excelFile, activities = []) { // , monthProvider = ''
      // card_number, card_pin, expiry_date, package_code

      return new Promise((resolve, reject) => {
        const dateFormat = 'YYYY-MM'

        const schema = {
          'Officer ID': {
            prop: 'officer_id',
            type: String
          },
          'Officer Name': {
            prop: 'officer_name',
            type: String
          },
          'Activity Name': {
            prop: 'activity_name',
            type: activityName => {
              const activityNames = activities.map(
                activity => activity.activity_name
              )

              if (!activityNames.includes(activityName)) {
                throw new Error(`( ${activityName} ) is invalid, please ensure it matches one of our existing activities.`)
              }
              return activityName
            }
          },
          'Number of Events': {
            prop: 'event_plan',
            type: Number,
            required: true
          }
        }

        readXlsxFile(excelFile, { schema, dateFormat }).then(
          validatedData => {
            const { rows, errors } = validatedData
            if (rows.length <= 0) {
              this.swalInvalid(
                [
                  '<div class="text-left">',
                  'No valid Data found in the uploaded file.',
                  '<br /><br />Please ensure all rows adhere to the required format:',
                  '<br />A. Activity Name (text)',
                  '<br />B. Crop Name (text)',
                  '<br />C. Number of Events (number)',
                  '</div>'
                ].join(''),
                'Invalid Data Format'
              )
            }

            if (errors.length) {
              this.swalInvalid(
                [
                  '<div class="text-left">',
                  'The uploaded file contains errors.',
                  '<br /><br />Review the <strong class="text-danger">Invalid Imports</strong> section for details',
                  'and correct the errors before retrying and ensure all rows adhere to the required format:',
                  '<br />A. Activity Name (text)',
                  '<br />B. Crop Name (text)',
                  '<br />C. Number of Events (number)',
                  '</div>'
                ].join(''),
                'Invalid Data Format'
              )
            }

            resolve({
              activities,
              errors,
              rows: rows.filter(row => Number(row.event_plan) > 0).map(
                (row, index) => {
                  const activity = activities.find(
                    activity => activity.activity_name === row.activity_name
                  )

                  return Object.assign(row, {
                    activity,
                    error: QUERY.find(errors, {
                      row: Number(index) + 2
                    })
                  })
                }
              )
            })
          }
        ).catch(() => {
          this.swalInvalid(
            [
              '<div class="text-left">',
              'The uploaded file is not a valid Excel file.',
              '<br /><br />Please ensure the file is in .xls or .xlsx format.',
              '<br /><br />Please ensure all headers adhere to the required format:',
              '<br />A. Activity Name (text)',
              '<br />B. Crop Name (text)',
              '<br />C. Number of Events (number)',
              '</div>'
            ].join(''),
            'Invalid File Format'
          )

          reject(new Error('Invalid File Format'))
        })
      })
    },

    validateImportedMultiTask (excelFile, activities = [], officers = []) {
      return new Promise((resolve, reject) => {
        const schema = {
          'Officer ID': {
            prop: 'officer_id',
            type: officerId => {
              const officerIds = officers.map(
                officer => officer.sso_id
              )

              if (!officerIds.includes(officerId)) {
                throw new Error(`( ${officerId} ) is invalid, please ensure it matches one of our existing officers.`)
              }

              return officerId
            },
            required: true
          },
          'Officer Name': {
            prop: 'officer_name',
            type: String
          },
          'Activity Name': {
            prop: 'activity_name',
            type: activityName => {
              const activityNames = activities.map(
                activity => activity.activity_name
              )

              if (!activityNames.includes(activityName)) {
                throw new Error(`( ${activityName} ) is invalid, please ensure it matches one of our existing activities.`)
              }

              return activityName
            }
          },
          'Number of Events': {
            prop: 'event_plan',
            type: Number,
            required: true
          }
        }

        readSheetNames(excelFile).then(async sheets => {
          if (sheets.length <= 0) {
            // Display an error message if no sheets are found
          }

          const combinedRows = []

          sheets.forEach(sheet => {
            const waitUntil = readXlsxFile(excelFile, { schema, sheet }).then(
              ({ rows, errors }) => ({
                sheet,
                activities,
                errors,
                rows: rows.filter(row => Number(row.event_plan) > 0).map(
                  (row, index) => {
                    const activity = activities.find(
                      activity => activity.activity_name === row.activity_name
                    )

                    return Object.assign(row, {
                      activity,
                      sso_id: row.officer_id,
                      error: QUERY.find(errors, {
                        row: Number(index) + 2
                      })
                    })
                  }
                )
              })
            )

            combinedRows.push(waitUntil)
          })

          await Promise.all(combinedRows).then(resolve)
        }).catch(() => {
          reject(new Error('Invalid File Format'))
        })
      })
    }
  }
}
